import React, { createContext, useState, useEffect, ReactNode, useContext } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { sendDataAPI } from '@lib/sendDataAPI';
import { useSession } from '@context/sessionProvider';

declare global {
	interface Window {
		amplitudeInitialized?: boolean;
	}
}

let amplitudeInitializationPromise: Promise<typeof amplitude | null> | null = null;
let amplitudeInitializationContext: string | null = null;
let sessionReplayPluginAdded = false;

interface AmplitudeContextValue {
	amplitudeInstance: typeof amplitude | null;
}

const AmplitudeContext = createContext<AmplitudeContextValue>({
	amplitudeInstance: null,
});

export const useAmplitude = () => useContext(AmplitudeContext);

interface AmplitudeProviderProps {
	children: ReactNode;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

export const AmplitudeProvider: React.FC<AmplitudeProviderProps> = ({ children, onLoad, onError }) => {
	const [amplitudeInstance, setAmplitudeInstance] = useState<typeof amplitude | null>(null);
	const { context } = useSession();

	useEffect(() => {
		let isMounted = true;

		const initializeAmplitude = async () => {
			try {
				const instance = await getAmplitudeInstance(context);

				if (isMounted && instance) {
					setAmplitudeInstance(instance);
				}

				if (isMounted && onLoad) {
					onLoad();
				}
			} catch (error) {
				console.error('Failed to initialize Amplitude', error);
				if (isMounted && onError) {
					onError('Failed to initialize Amplitude. Please try again.');
				}
			}
		};

		initializeAmplitude();

		return () => {
			isMounted = false;
		};
	}, [context, onLoad, onError]);

	return <AmplitudeContext.Provider value={{ amplitudeInstance }}>{children}</AmplitudeContext.Provider>;
};

async function getAmplitudeInstance(context: string): Promise<typeof amplitude | null> {
	if (typeof window === 'undefined') {
		return null;
	}

	if (window.amplitudeInitialized) {
		return amplitude;
	}

	if (!amplitudeInitializationPromise || amplitudeInitializationContext !== context) {
		amplitudeInitializationContext = context;
		amplitudeInitializationPromise = initializeAmplitudeClient(context).catch((error) => {
			if (amplitudeInitializationContext === context) {
				amplitudeInitializationPromise = null;
				amplitudeInitializationContext = null;
				sessionReplayPluginAdded = false;
			}

			throw error;
		});
	}

	return amplitudeInitializationPromise;
}

async function initializeAmplitudeClient(context: string): Promise<typeof amplitude | null> {
	const response = await sendDataAPI({ jwtToken: context }, '/api/amplitude/get', 'GET');

	if (!response.ok) {
		throw new Error('Failed to fetch Amplitude configuration.');
	}

	const responseBody = await response.json();
	const { is_enabled, key, bigcommerce_id } = responseBody.data;

	if (!is_enabled) {
		return null;
	}

	amplitude.init(key, bigcommerce_id, { autocapture: true });

	if (!sessionReplayPluginAdded) {
		sessionReplayPluginAdded = true;
		const sessionReplayTracking = sessionReplayPlugin();
		await amplitude.add(sessionReplayTracking).promise;
	}

	window.amplitudeInitialized = true;

	return amplitude;
}
